import { forwardRef, ForwardRefRenderFunction, useImperativeHandle, useMemo, useRef, useState } from 'react';

import { RubberForm } from '@/features/rubber-form/ui/rubber-form';
import { SideModal } from '@/shared/components';
import { showErrorToast, showSuccessToast } from '@/shared/components/toasts';
import { Button, ButtonGroup, useToast } from '@chakra-ui-kraud/react';

import {
	ProductOperationTypesCatalog,
	RubberCatalogResponse,
	useCreateRubberMutation,
	useUpdateRubberMutation,
	useAddFileToRubberMutation,
} from '@/shared/state/api/swagger';
import { RubberModalProps, RubberFormRef } from '../model/model';

export interface RubberModalRef {
	submitForm: () => void;
}

const RubberModalComponent: ForwardRefRenderFunction<RubberModalRef, RubberModalProps> = (
	{ isOpen, onClose, title, variant, onBlur, selectedProduct },
	ref,
) => {
	const toast = useToast();
	const operations = useMemo(() => selectedProduct?.operation_types ?? [], [selectedProduct]);

	const [operation_types, setOperationType] = useState<ProductOperationTypesCatalog[]>([]);
	const formRef = useRef<RubberFormRef>(null);

	useImperativeHandle(ref, () => ({
		submitForm: () => {
			formRef.current?.submitForm();
		},
	}));

	const [createRubber, { isLoading: isCreateRubberLoading }] = useCreateRubberMutation();
	const [updateRubber, { isLoading: isUpdateRubberLoading }] = useUpdateRubberMutation();
	const [addFileToRubber] = useAddFileToRubberMutation();

	const handleSubmit = (data: RubberCatalogResponse) => {
		const standard_consumption_grams = data.standard_consumption_grams
			? Number(String(data.standard_consumption_grams).replace(',', '.'))
			: '';

		const weighed_amount =
			data.weighed_amount && String(data.weighed_amount).trim() !== ''
				? Number(String(data.weighed_amount).replace(',', '.'))
				: undefined;

		if (variant === 'create') {
			createRubber({
				rubberCatalogCreateRequest: {
					...data,
					standard_consumption_grams: Number(standard_consumption_grams),
					weighed_amount,
					operation_types:
						operation_types
							?.filter((el) => el.name)
							.map((el, index) => ({ name: el.name ?? '', step_id: (index + 1) * 5 })) ?? [],
				},
			})
				.unwrap()
				.then((rubber) => {
					if (data?.file && data?.file instanceof Blob && rubber?.id) {
						return addFileToRubber({
							bodyAddFileToRubber: { file: data.file },
							rubberId: rubber?.id,
						});
					}
				})
				.then(() => {
					showSuccessToast(toast, { description: 'РТИ создана' });
					onClose();
				})
				.catch((e) => {
					if (e.status === 422 && !operation_types?.length) {
						showErrorToast(toast, {
							description: 'Для создания РТИ необходимо заполнить блок операции',
						});
					} else {
						showErrorToast(toast, {
							description: 'При создании РТИ произошла ошибка',
						});
					}
				});
		}

		if (variant === 'edit' && selectedProduct?.id) {
			Promise.all([
				updateRubber({
					rubberId: data.id,
					rubberCatalogUpdateRequest: {
						...data,
						standard_consumption_grams: Number(standard_consumption_grams),
						weighed_amount,
						operation_types:
							operation_types
								?.filter((el) => el.name)
								.map((el, index) => ({ name: el.name ?? '', step_id: (index + 1) * 5 })) ?? [],
					},
				}),
				data?.file &&
					data?.file instanceof Blob &&
					addFileToRubber({
						bodyAddFileToRubber: { file: data.file },
						rubberId: data.id,
					}),
			])
				.then(() => {
					showSuccessToast(toast, { description: 'РТИ обновлена' });
					onClose();
				})
				.catch((e) => {
					showErrorToast(toast, { description: 'При обновлении РТИ произошла ошибка' });
				});
		}
	};

	return (
		<SideModal
			title={title}
			isOpen={isOpen}
			onClose={onClose}
			maxWidth={'644px'}
			footer={
				<ButtonGroup>
					<Button size="md" variant="ghost" colorScheme="tertiary" onClick={onClose}>
						Отменить
					</Button>
					<Button
						size="md"
						variant="solid"
						colorScheme="primary"
						onClick={() => {
							formRef?.current?.submitForm();
						}}
						isDisabled={isCreateRubberLoading || isUpdateRubberLoading}
					>
						{variant === 'edit' ? 'Сохранить' : 'Добавить деталь'}
					</Button>
				</ButtonGroup>
			}
			onBlur={onBlur ?? onClose}
		>
			<RubberForm
				onSubmit={handleSubmit}
				ref={formRef}
				selectedProduct={selectedProduct}
				operations={operations}
				setOperationType={setOperationType}
			/>
		</SideModal>
	);
};

export const RubberModal = forwardRef<RubberModalRef, RubberModalProps>(RubberModalComponent);
